import { useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { Api } from '../../lib/apiClient'

const DATE_TYPES = [
  { key: 'solo', label: 'Solo', icon: '🙋' },
  { key: 'romantic', label: 'Romantic', icon: '💘' },
  { key: 'firstDate', label: 'First Date', icon: '🌟' },
  { key: 'casual', label: 'Casual', icon: '🙂' },
  { key: 'fun', label: 'Fun', icon: '🎉' },
  { key: 'active', label: 'Active', icon: '🏃' },
  { key: 'lunch', label: 'Lunch', icon: '🥪' },
  { key: 'dinner', label: 'Dinner', icon: '🍽️' },
  { key: 'nightOut', label: 'Night Out', icon: '🌙' },
]

export default function DateTypeSelector() {
  const { isAuthenticated } = useAuth()
  const [selected, setSelected] = useState<string[]>(() => {
    const raw = localStorage.getItem('ds_prefs')
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        return parsed?.dateTypes || []
      } catch {}
    }
    return []
  })
  const [justSaved, setJustSaved] = useState(false)

  useEffect(() => {
    const raw = localStorage.getItem('ds_prefs')
    let prefs = {}
    try { prefs = raw ? JSON.parse(raw) : {} } catch {}
    localStorage.setItem('ds_prefs', JSON.stringify({ ...(prefs as any), dateTypes: selected }))
  }, [selected])

  // Prefill from backend if available (only for authenticated users)
  useEffect(() => {
    if (!isAuthenticated) return
    
    ;(async () => {
      try {
        const data = await Api.preferences.get()
        if (Array.isArray(data?.preferences?.dateTypes)) {
          setSelected(data.preferences.dateTypes)
        }
      } catch (e) {
        console.error('Failed to load date types:', e)
      }
    })()
  }, [isAuthenticated])

  const toggle = (key: string) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])
  }

  const saveToBackend = async () => {
    // For guest users, just show saved message (data is already in localStorage)
    if (!isAuthenticated) {
      setJustSaved(true)
      setTimeout(() => setJustSaved(false), 1200)
      return
    }
    
    try {
      await Api.preferences.save({ preferences: { dateTypes: selected } })
      setJustSaved(true)
      setTimeout(() => setJustSaved(false), 1200)
    } catch (e) {
      console.error('Failed to save date types:', e)
    }
  }

  return (
    <div className="glass-card p-4">
      <div className="flex items-center justify-between relative">
        <h2 className="text-xl font-semibold text-black">Date Types</h2>
        <button className="btn-primary px-4 py-2" onClick={saveToBackend}>Save</button>
        {justSaved && (
          <span className="absolute right-0 -top-6 text-xs bg-white/90 border border-black/10 rounded-full px-3 py-1 shadow">
            Saved
          </span>
        )}
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {DATE_TYPES.map(({ key, label, icon }) => {
          const isSelected = selected.includes(key)
          return (
            <button
              key={key}
              className={`rounded-2xl px-4 py-6 border transition-all duration-200 shadow flex flex-col items-center justify-center text-center min-h-[112px] ${isSelected ? 'bg-brand-orange/90 border-brand-orange' : 'bg-white/80 border-black/5 hover:shadow-lg'} hover:-translate-y-0.5`}
              onClick={() => toggle(key)}
              aria-pressed={isSelected}
            >
              <div className="text-2xl" aria-hidden>{icon}</div>
              <div className="mt-2 font-semibold text-black">{label}</div>
            </button>
          )
        })}
      </div>
    </div>
  )
}


